import { motion, useMotionValue, animate } from 'framer-motion'
import { useRef, useEffect } from 'react'
import './FortuneWheel.css'
import { SpinPrizeSector } from '../../../../types/api'
import { Triangle } from 'lucide-react'

interface FortuneWheelProps {
  sectors: SpinPrizeSector[]
  isSpinning: boolean
  winningIndex: number | null
  onSpinComplete?: () => void
}

const FortuneWheel = ({
  sectors,
  isSpinning,
  winningIndex,
  onSpinComplete,
}: FortuneWheelProps) => {
  const x = useMotionValue(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemWidth = 239
  const gap = 8 // gap между элементами из CSS
  const itemWithGap = itemWidth + gap // полная ширина элемента с учетом gap

  // Сортируем секторы один раз
  const sortedSectors = [...sectors].sort((a, b) => a.sort - b.sort)

  // Устанавливаем начальное центрированное положение
  useEffect(() => {
    if (!isSpinning && sortedSectors.length > 0) {
      // Показываем второй приз (индекс 1) в центре экрана
      // Центр экрана минус центр карточки (itemWidth/2) минус смещение на один элемент
      const screenCenter = window.innerWidth / 2
      const initialX = screenCenter - itemWidth / 1.75 - itemWithGap
      x.set(initialX)
    }
  }, [sortedSectors.length, isSpinning, itemWidth, itemWithGap, x])

  useEffect(() => {
    if (isSpinning && winningIndex !== null && sortedSectors.length > 0) {
      const centerX = window.innerWidth / 2 - itemWidth / 1.75

      // Целевой индекс в повторяющемся массиве (четвертое повторение + winningIndex)
      // Делаем 3 полных оборота и останавливаемся на выигрышном призе
      const targetIndexInRepeated = sortedSectors.length * 3 + winningIndex

      // Рассчитываем финальную позицию (центр выигрышной карточки)
      const finalX = centerX - targetIndexInRepeated * itemWithGap

      // Позиция "переезда" - на половину карточки дальше
      const overshootX = finalX - itemWithGap * 0.6

      // Сначала едем до "переезда" за 9.5 секунд
      animate(x, overshootX, {
        duration: 9.5,
        ease: [0.25, 0.1, 0.25, 1],
        onComplete: () => {
          // Потом возвращаемся на финальную позицию за 0.5 секунды
          animate(x, finalX, {
            duration: 0.5,
            ease: [0.25, 0.46, 0.45, 0.94],
            onComplete: () => {
              if (onSpinComplete) {
                setTimeout(onSpinComplete, 500)
              }
            },
          })
        },
      })
    }
  }, [
    isSpinning,
    winningIndex,
    sortedSectors.length,
    itemWidth,
    itemWithGap,
    x,
    onSpinComplete,
  ])

  if (sectors.length === 0) {
    return (
      <div className="fortune-wheel-empty">
        <p>Нет доступных призов</p>
      </div>
    )
  }

  // Создаем массив призов для бесконечной прокрутки (повторяем 5 раз)
  const repeatedSectors = [
    ...sortedSectors,
    ...sortedSectors,
    ...sortedSectors,
    ...sortedSectors,
    ...sortedSectors,
  ]

  return (
    <div className="fortune-wheel-container" ref={containerRef}>
      <div className="fortune-wheel-indicator top">
        <div className="indicator-background">
          <Triangle
            size={24}
            fill="#7AA9C7"
            stroke="#7AA9C7"
            style={{ transform: 'rotate(180deg)' }}
          />
        </div>
      </div>

      <div className="fortune-wheel-indicator bottom">
        <div className="indicator-background">
          <Triangle size={24} fill="#7AA9C7" stroke="#7AA9C7" />
        </div>
      </div>

      <motion.div className="fortune-wheel-track" style={{ x }}>
        {repeatedSectors.map((sector, index) => (
          <div
            key={`${sector.spin_sector_id}-${index}`}
            className="fortune-wheel-item"
            style={{ width: `${itemWidth}px` }}
          >
            <div className="wheel-item-content">
              <img
                src={sector.icon_url}
                alt={sector.header_text || 'Prize'}
                className="wheel-item-icon"
              />
              <h3 className="wheel-name">{sector.header_text || 'Приз'}</h3>
              <p className="wheel-description">{sector.text || ''}</p>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  )
}

export default FortuneWheel
